import { useIsExperimentActivated } from '@99designs/experiments';
import { formatMessage } from '@99designs/frontend-components/i18n';
import { CurrencyCode } from '@99designs/i18n';
import { getSavings } from '../util/formatPrice';
import { getStartingPriceForProduct } from './getStartingPriceForProduct';
import type { ProductCardProps } from '@99designs/marketing-ui';
import type { ProductGroupPrice } from '../types';

export const products = (
    productGroupPrices: ProductGroupPrice[],
    siteCurrency: CurrencyCode,
    locale: string,
    isNewPackageExperiment = false
): ProductCardProps[] => {
    const relatedPacks = [
        {
            url: 'logo-website-squarespace',
            title: __('Logo & website'),
            description: __(
                'Start getting clicks when you take your logo design online with a fully functional and custom Squarespace website that also includes uploading content.'
            ),
            price: {
                currency: CurrencyCode.USD,
                amountInCents: 139900,
            },
            saving: formatMessage(__('Save {price}'), { price: '$300' }),
            image: {
                src: 'https://99designs-start-static.imgix.net/product-details/related-products/logo-website.jpg',
                alt: 'some alt text',
                designerAttribution: { text: '', userId: '' },
            },
        },
        {
            url: 'logo-design/details',
            title: __('Logo design'),
            description: __(
                'Get a fresh logo for your business and grow your branding with professional, custom design.'
            ),
            price: getStartingPriceForProduct(productGroupPrices, 'logo-design', siteCurrency),
            saving: null,
            image: {
                src: 'https://99designs-start-static.imgix.net/product-details/related-products/logo-design.jpeg',
                alt: '',
                designerAttribution: { text: 'GS | Designs', userId: '977680' },
            },
        },
        {
            url: 'logo-brand-guide/details',
            title: __('Logo & brand guide'),
            description: __(
                'Start your branding the right way with a logo design and brand guide that includes fonts, colors and styling.'
            ),
            price: getStartingPriceForProduct(productGroupPrices, 'logo-brand-guide', siteCurrency),
            saving: getSavings('logo-brand-guide', siteCurrency, locale),
            image: {
                src: 'https://99designs-start-static.imgix.net/product-details/related-products/logo-brand-guide.jpeg',
                alt: '',
                designerAttribution: { text: 'Iris Design', userId: '2778758' },
            },
        },
    ];

    const currentLivePacks = [
        {
            url: 'brand-identity-pack/details',
            title: __('Logo & brand identity pack'),
            description: __(
                'Get all the digital and print essentials you need to kick-start your business. Includes a logo, business card, letterhead, envelope and Facebook cover.'
            ),
            price: getStartingPriceForProduct(
                productGroupPrices,
                'brand-identity-pack',
                siteCurrency
            ),
            saving: getSavings('logo-brand-identity-pack', siteCurrency, locale),
            image: {
                src: 'https://99designs-start-static.imgix.net/product-details/related-products/brand-identity-pack.jpeg',
                alt: '',
                designerAttribution: { text: 'Satryo', userId: '3100340' },
            },
        },
        {
            url: 'logo-business-card-design/details',
            title: __('Logo & business card'),
            description: __(
                'Pair your custom logo design with business cards so good you won’t want to give them away!'
            ),
            price: getStartingPriceForProduct(
                productGroupPrices,
                'logo-business-card-design',
                siteCurrency
            ),
            saving: getSavings('logo-business-card-design', siteCurrency, locale),
            image: {
                src: 'https://99designs-start-static.imgix.net/product-details/related-products/logo-business-card-design.jpeg',
                alt: '',
                designerAttribution: { text: 'artsigma', userId: '1284917' },
            },
        },
        {
            url: 'social-media-pack/details',
            title: __('Logo & social media pack'),
            description: __(
                'Add social banners to your logo and keep your business on-trend. Includes a logo, Facebook cover, Twitter header and YouTube background.'
            ),
            price: getStartingPriceForProduct(
                productGroupPrices,
                'social-media-pack',
                siteCurrency
            ),
            saving: getSavings('logo-social-media-pack', siteCurrency, locale),
            image: {
                src: 'https://99designs-start-static.imgix.net/product-details/related-products/social-media-pack.jpeg',
                alt: '',
                designerAttribution: { text: 'Suparde', userId: '3004243' },
            },
        },
    ];

    const newBrandPacks = [
        {
            url: 'brand-launch-pack/details',
            title: 'Brand Launch Package',
            description: __placeholder(
                'The perfect way to launch your new venture – get a logo, brand guide plus digital and print essentials that’ll get you noticed.'
            ),
            price: getStartingPriceForProduct(
                productGroupPrices,
                'brand-launch-pack',
                siteCurrency
            ),
            saving: getSavings('brand-launch-pack', siteCurrency, locale),
            image: {
                src: 'https://99designs-start-static.imgix.net/product-details/related-products/brand-identity-pack.jpeg',
                alt: '',
                designerAttribution: { text: 'Satryo', userId: '3100340' },
            },
        },
        {
            url: 'brand-starter-pack/details',
            title: 'Brand Starter Package',
            description:
                'Get a stunning logo, business card and social assets to make a strong impression on and offline.',
            price: getStartingPriceForProduct(
                productGroupPrices,
                'brand-starter-pack',
                siteCurrency
            ),
            saving: getSavings('brand-starter-pack', siteCurrency, locale),
            image: {
                src: 'https://99designs-start-static.imgix.net/product-details/related-products/social-media-pack.jpeg',
                alt: '',
                designerAttribution: { text: 'Suparde', userId: '3004243' },
            },
        },
    ];

    if (isNewPackageExperiment) {
        return newBrandPacks.concat(relatedPacks);
    }
    return currentLivePacks.concat(relatedPacks);
};
